import React, { useEffect, useRef, useState } from 'react';
import { ExposureTypes, getExposreDesc, getExposureColor } from '../../utils/RiskUtils';
import { Visibility } from '@mui/icons-material';
import './MaterialTrendButton.scss';

export interface singleMaterialCardProps {
  materialName: string;
  formulaId: string;
  quoteSourceSymbol?: string | null;
  exposureStatus: ExposureTypes;
  isSelected?: boolean;
  cardColor?: string;
  compact?: boolean;
  disabled?: boolean;
  isPriceWatchOnly?: boolean;
}

const SingleMaterialCard: React.FC<singleMaterialCardProps> = ({
  materialName,
  formulaId,
  quoteSourceSymbol,
  cardColor,
  exposureStatus,
  isSelected,
  compact,
  disabled,
  isPriceWatchOnly,
}) => {
  const secondaryLabel = quoteSourceSymbol || formulaId;
  const exposureBaseColor = getExposureColor(exposureStatus);
  const exposureDotColor = hexToRgba(exposureBaseColor, 0.45);

  if (compact) {
    // const badgeBg = cardColor ? hexToRgba(cardColor, 0.18) : '#f3f4f6';

    return (
      <div
        className={`single-material-card flex flex-row items-center shrink-0 px-3 py-1.5 my-1 rounded ${
          disabled 
            ? 'opacity-40 cursor-not-allowed border-l-4' 
            : isSelected 
              ? 'border-l-[6px] font-semibold outline-none ring-0' 
              : 'border-l-4 shadow-custom hover:bg-gray-75 transform hover:scale-105'
        } transition-all duration-300 focus:outline-none`}
        style={{ 
          borderColor: cardColor, 
          boxShadow: disabled ? 'none' : isSelected ? `0 2px 8px ${cardColor}40, 0 0 0 1px ${cardColor}30` : '0 0 0.5rem #ccc',
          backgroundColor: disabled ? '#f5f5f5' : isSelected ? `${cardColor}25` : 'white',
          pointerEvents: disabled ? 'none' : 'auto',
        } as React.CSSProperties}
      >
        {/* Temporarily hidden symbol badge */}
        {/* <span
          className="inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold text-gray-800 mr-2"
          style={{ backgroundColor: badgeBg }}
        >
          {secondaryLabel}
        </span> */}
        <span className="text-[12px] text-gray-800 truncate flex-1">{materialName}</span>
        {isPriceWatchOnly ? (
          <Visibility sx={{ fontSize: 14 }} className="ml-2 flex-shrink-0 text-gray-300" />
        ) : (
          <div
            style={{ backgroundColor: exposureDotColor }}
            className="w-3 h-3 rounded-full ml-2 flex-shrink-0"
          ></div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`single-material-card flex flex-col shrink-0 p-2 my-1 rounded border-l-8 ${
        disabled 
          ? 'opacity-40 cursor-not-allowed' 
          : isSelected 
            ? 'ring-2 ring-offset-1 shadow-lg hover:bg-gray-75 transform hover:scale-105' 
            : 'shadow-custom hover:shadow-custom hover:bg-gray-75 transform hover:scale-105'
      } transition-all duration-300`}
      style={{ 
        borderColor: cardColor, 
        boxShadow: disabled ? 'none' : isSelected ? `0 0 1rem ${cardColor}40` : '0 0 0.5rem #ccc',
        '--tw-ring-color': cardColor,
        backgroundColor: disabled ? '#f5f5f5' : isSelected ? `${cardColor}10` : 'white',
        pointerEvents: disabled ? 'none' : 'auto',
      } as React.CSSProperties}
    >
      <div className="flex flex-row justify-between align-middle">
        <div className="flex flex-col mr-2 text-left overflow-hidden max-w-full">
          <div className="material-name-marquee">
            <span className="material-name-text truncate">{materialName}</span>
          </div>
          {/* Temporarily hidden symbol label */}
          {/* <p className="text-xs text-gray-600 m-0">{secondaryLabel}</p> */}
        </div>
        <div
          style={{ backgroundColor: exposureDotColor }}
          className="w-3.5 h-3.5 rounded-full self-center"
        ></div>
      </div>
    </div>
  );
};

const hexToRgba = (hex: string, alpha: number): string => {
  let cleaned = hex.replace('#', '');

  if (cleaned.length === 3) {
    cleaned = cleaned
      .split('')
      .map((c) => c + c)
      .join('');
  }

  const r = parseInt(cleaned.substring(0, 2), 16) || 0;
  const g = parseInt(cleaned.substring(2, 4), 16) || 0;
  const b = parseInt(cleaned.substring(4, 6), 16) || 0;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

interface MaterialTrendButtonProps {
  buttons: singleMaterialCardProps[];
  currentMaterial: string;
  setCurrentMaterial: (_materialName: string) => void;
  compact?: boolean;
}

export const MaterialTrendButton: React.FC<MaterialTrendButtonProps> = ({
  buttons,
  currentMaterial,
  setCurrentMaterial,
  compact,
}) => {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateScrollButtons = () => {
      const current = scrollContainerRef.current;
      if (!current) return;

      setCanScrollLeft(current.scrollLeft > 0);
      setCanScrollRight(current.scrollLeft + current.clientWidth < current.scrollWidth);
    };

    updateScrollButtons();
    const current = scrollContainerRef.current;
    current?.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      current?.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [buttons]);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  return (
    <div className="flex w-full ">
      {canScrollLeft && (
        <div className="flex flex-col min-w-[2.35rem] shadow-md me-[0.3rem]">
          <button onClick={scrollLeft} className="bg-white rounded h-full disabled:opacity-50">
            <img className="h-full w-full" title="left" src="/assets/icons/flat/chevron_left.svg" />
          </button>
        </div>
      )}
      <div ref={scrollContainerRef} className="flex gap-[0.75rem] px-2  whitespace-nowrap overflow-hidden">
        {buttons.map((material, index) => (
          <button
            key={index}
            title={`switch to ${material.materialName} view (${getExposreDesc(material.exposureStatus)})`}
            className="rounded focus:outline-none focus:ring-0"
            onClick={() => setCurrentMaterial(material.formulaId)}
            disabled={material.disabled}
          >
            <SingleMaterialCard
              formulaId={material.formulaId}
              materialName={material.materialName}
              quoteSourceSymbol={material.quoteSourceSymbol}
              cardColor={material.cardColor}
              isSelected={
                material.formulaId === currentMaterial || (!currentMaterial && material.formulaId === 'general')
              }
              exposureStatus={material.exposureStatus}
              compact={compact}
              disabled={material.disabled}
              isPriceWatchOnly={material.isPriceWatchOnly}
            />
          </button>
        ))}
      </div>

      {canScrollRight && (
        <div className="flex flex-col min-w-[2.35rem] shadow-md ms-[0.3rem]">
          <button onClick={scrollRight} className="bg-white rounded h-full disabled:opacity-50">
            <img className="h-full w-full" title="right" src="/assets/icons/flat/chevron_right.svg" />
          </button>
        </div>
      )}
    </div>
  );
};
